import React, { useState, useEffect } from 'react';
import { getPaymentMethodStats, getCompletionTimeStats, getStatusDistribution } from '../api/reportApi';
import { toast } from 'react-toastify';
import { 
  BarChart, 
  CreditCard, 
  Clock, 
  PieChart, 
  RefreshCw, 
  AlertTriangle, 
  Check,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

// Stat kartı komponenti
const StatCard = ({ title, value, icon, color, subtext, isLoading }) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-full ${color} text-white`}>
          {icon}
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          {isLoading ? (
            <div className="h-6 w-24 bg-gray-200 animate-pulse rounded"></div>
          ) : (
            <p className="text-xl font-semibold">{value}</p>
          )}
          {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
        </div>
      </div>
    </div>
  );
};

// Ödeme yöntemleri tablosu komponenti
const PaymentMethodsTable = ({ data, isLoading }) => {
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <CreditCard size={18} className="mr-2 text-primary-500" />
          Ödeme Yöntemlerine Göre İşlemler
        </h3>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ödeme Yöntemi</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İşlem Sayısı</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yüzde</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Toplam Tutar</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ort. Tutar</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              Array(3).fill().map((_, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-12 animate-pulse"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-12 animate-pulse"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
                  </td>
                </tr>
              ))
            ) : data?.payment_methods?.length > 0 ? (
              data.payment_methods.map((method) => (
                <tr key={method.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{method.method_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{method.refund_count}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {parseFloat(method.percentage).toFixed(1)}%
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div 
                          className="bg-primary-500 h-2 rounded-full" 
                          style={{ width: `${method.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {method.total_amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {method.average_amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                  Henüz veri bulunmuyor
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Tamamlanma süreleri tablosu komponenti
const CompletionTimesTable = ({ data, isLoading }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <Clock size={18} className="mr-2 text-primary-500" />
          İşlem Tamamlanma Süreleri
        </h3>
      </div>
      
      {data?.overall_stats && (
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="text-sm font-medium text-gray-500">Genel Ortalama Tamamlanma Süresi:</span>
              <span className="ml-2 text-lg font-semibold text-gray-900">
                {data.overall_stats.avg_completion_formatted}
              </span>
            </div>
            <div className="mt-2 sm:mt-0">
              <span className="text-sm text-gray-500">
                Tamamlanan İşlem Sayısı: {data.overall_stats.completed_count}
              </span>
            </div>
          </div>
          
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded p-3 border">
              <div className="text-xs font-medium text-gray-500 mb-1">Minimum Süre</div>
              <div className="text-sm font-medium">
                {parseFloat(data.overall_stats.min_completion_hours) < 24 ? 
                  `${parseFloat(data.overall_stats.min_completion_hours).toFixed(1)} saat` : 
                  `${parseFloat(data.overall_stats.min_completion_days).toFixed(1)} gün`}
              </div>
            </div>
            <div className="bg-white rounded p-3 border">
              <div className="text-xs font-medium text-gray-500 mb-1">Ortalama Süre</div>
              <div className="text-sm font-medium">
                {parseFloat(data.overall_stats.avg_completion_hours) < 24 ? 
                  `${parseFloat(data.overall_stats.avg_completion_hours).toFixed(1)} saat` : 
                  `${parseFloat(data.overall_stats.avg_completion_days).toFixed(1)} gün`}
              </div>
            </div>
            <div className="bg-white rounded p-3 border">
              <div className="text-xs font-medium text-gray-500 mb-1">Maksimum Süre</div>
              <div className="text-sm font-medium">
                {parseFloat(data.overall_stats.max_completion_hours) < 24 ? 
                  `${parseFloat(data.overall_stats.max_completion_hours).toFixed(1)} saat` : 
                  `${parseFloat(data.overall_stats.max_completion_days).toFixed(1)} gün`}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ödeme Yöntemi</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tamamlanan İşlem</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ort. Tamamlanma Süresi</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min Süre</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Maks Süre</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              Array(3).fill().map((_, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-12 animate-pulse"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                  </td>
                </tr>
              ))
            ) : data?.method_completion_times?.length > 0 ? (
              <>
                {data.method_completion_times
                  .slice(0, isExpanded ? data.method_completion_times.length : 3)
                  .map((method) => (
                    <tr key={method.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{method.method_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{method.completed_count}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {method.avg_completion_formatted}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1 max-w-xs">
                          <div 
                            className="bg-green-500 h-1.5 rounded-full" 
                            style={{ 
                              width: `${Math.min(100, (parseFloat(method.avg_completion_hours) / 
                              (data.method_completion_times[data.method_completion_times.length-1].avg_completion_hours) * 100))}%` 
                            }}
                          ></div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {parseFloat(method.min_completion_hours) < 24 ? 
                            `${parseFloat(method.min_completion_hours).toFixed(1)} saat` : 
                            `${parseFloat(method.min_completion_days).toFixed(1)} gün`}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {parseFloat(method.max_completion_hours) < 24 ? 
                            `${parseFloat(method.max_completion_hours).toFixed(1)} saat` : 
                            `${parseFloat(method.max_completion_days).toFixed(1)} gün`}
                        </div>
                      </td>
                    </tr>
                ))}
                
                {data.method_completion_times.length > 3 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-2 text-center">
                      <button 
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="text-primary-500 text-sm flex items-center mx-auto hover:text-primary-700"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp size={16} className="mr-1" />
                            Daha az göster
                          </>
                        ) : (
                          <>
                            <ChevronDown size={16} className="mr-1" />
                            {data.method_completion_times.length - 3} yöntem daha göster
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                )}
              </>
            ) : (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                  {data?.message || 'Henüz tamamlanmış işlem bulunmuyor'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Durum dağılımı tablosu komponenti
const StatusDistributionTable = ({ data, isLoading }) => {
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <PieChart size={18} className="mr-2 text-primary-500" />
          Durum Dağılımı
        </h3>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İşlem Sayısı</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yüzde</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              Array(4).fill().map((_, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-12 animate-pulse"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                  </td>
                </tr>
              ))
            ) : data?.status_distribution?.length > 0 ? (
              data.status_distribution.map((status) => (
                <tr key={status.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2" 
                        style={{ backgroundColor: status.color || '#808080' }}
                      ></div>
                      <div className="text-sm font-medium text-gray-900">
                        {status.status_name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{status.refund_count}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {parseFloat(status.percentage).toFixed(1)}%
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1 max-w-xs">
                        <div 
                          className="h-2 rounded-full" 
                          style={{ 
                            width: `${status.percentage}%`,
                            backgroundColor: status.color || '#808080'
                          }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">
                  Henüz veri bulunmuyor
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ReportsPage = () => {
  const [methodStats, setMethodStats] = useState(null);
  const [completionTimeStats, setCompletionTimeStats] = useState(null);
  const [statusStats, setStatusStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Tüm istatistikleri yükle
  const loadAllStats = async () => {
    setIsLoading(true);
    
    try {
      // Paralel olarak tüm istatistikleri getir
      const [methodData, completionData, statusData] = await Promise.all([
        getPaymentMethodStats(),
        getCompletionTimeStats(),
        getStatusDistribution()
      ]);
      
      setMethodStats(methodData);
      setCompletionTimeStats(completionData);
      setStatusStats(statusData);
      setLastUpdated(new Date());
    } catch (error) {
      toast.error(`İstatistikler yüklenirken hata: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Sayfa yüklendiğinde istatistikleri al
  useEffect(() => {
    loadAllStats();
  }, []);
  
  // Yenile butonu işlevi
  const handleRefresh = () => {
    loadAllStats();
    toast.success('İstatistikler güncelleniyor...');
  };
  
  return (
    <div className="py-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">İade Raporları</h1>
          <p className="text-gray-600">
            İade işlemlerinin istatistiksel analizleri ve performans metrikleri
          </p>
        </div>
        
        <div className="mt-3 sm:mt-0 flex items-center">
          <button
            onClick={handleRefresh}
            className="px-4 py-2 flex items-center text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <RefreshCw size={16} className="mr-1" />
            Yenile
          </button>
          
          {lastUpdated && (
            <span className="ml-3 text-sm text-gray-500">
              Son güncelleme: {format(lastUpdated, 'dd MMM HH:mm', { locale: tr })}
            </span>
          )}
        </div>
      </div>
      
      {/* Özet istatistikler */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard 
          title="Toplam İade İşlem Sayısı" 
          value={methodStats?.total_refunds || 0}
          icon={<BarChart size={20} />}
          color="bg-blue-500"
          isLoading={isLoading}
        />
        
        <StatCard 
          title="Toplam İade Tutarı" 
          value={`${methodStats?.total_amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || 0} TL`}
          icon={<CreditCard size={20} />}
          color="bg-green-500"
          isLoading={isLoading}
        />
        
        <StatCard 
          title="Ortalama Tamamlanma Süresi" 
          value={completionTimeStats?.overall_stats?.avg_completion_formatted || 'Veri yok'} 
          icon={<Clock size={20} />}
          color="bg-purple-500"
          isLoading={isLoading}
        />
        
        <StatCard 
          title="Tamamlanan İşlemler" 
          value={completionTimeStats?.overall_stats?.completed_count || 0}
          icon={<Check size={20} />}
          color="bg-amber-500"
          subtext={methodStats?.total_refunds ? 
            `Tüm işlemlerin ${((completionTimeStats?.overall_stats?.completed_count / methodStats.total_refunds) * 100).toFixed(1)}%'si` : ''}
          isLoading={isLoading}
        />
      </div>
      
      {/* Uyarı mesajı - veri yoksa */}
      {!isLoading && methodStats?.total_refunds === 0 && (
        <div className="mb-6 bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-amber-700">
                Henüz işlem bulunmadığı için raporlar oluşturulamıyor.
                İşlem ekledikçe burada detaylı istatistikler görebileceksiniz.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Ana içerik */}
      <div className="space-y-6">
        {/* Ödeme yöntemleri istatistikleri */}
        <PaymentMethodsTable data={methodStats} isLoading={isLoading} />
        
        {/* Tamamlanma süreleri */}
        <CompletionTimesTable data={completionTimeStats} isLoading={isLoading} />
        
        {/* Durum dağılımı */}
        <StatusDistributionTable data={statusStats} isLoading={isLoading} />
      </div>
    </div>
  );
};

export default ReportsPage; 